import './MenuSubItem.less';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Dropdown,
  DropDownProps,
  Input,
  InputRef,
  MenuProps,
  Spin,
  Tooltip,
} from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import classNames from 'classnames';

type MenuSubItemProps = {
  label: string;
  editableTitle?: string;
  path: string;
  actions: MenuProps['items'];
  tooltip?: string;
  isLoading?: boolean;
  isReadOnly: boolean;
  isEditingTitle: boolean;
  onTitleChange: (title: string) => void;
  onAbortTitleChange: () => void;
};

const MenuSubItem: React.FC<MenuSubItemProps> = ({
  label,
  editableTitle,
  path,
  actions,
  tooltip,
  isLoading,
  isReadOnly,
  isEditingTitle,
  onTitleChange,
  onAbortTitleChange,
}) => {
  const ignoreEvent = useCallback(
    (e: React.SyntheticEvent) => e.stopPropagation(),
    []
  );
  const inputRef = useRef<InputRef>(null);
  useEffect(() => {
    if (isEditingTitle) {
      setEditingTitle(editableTitle !== undefined ? editableTitle : label);
      inputRef.current?.focus();
    }
  }, [isEditingTitle]);

  const [editingTitle, setEditingTitle] = useState<string>('');

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingTitle(e.target.value);
  };

  const handleTitleSubmit = useCallback(async () => {
    const trimmedTitle = editingTitle.trim();
    onTitleChange(trimmedTitle);
  }, [onTitleChange, editingTitle]);

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      setEditingTitle('');
      onAbortTitleChange();
    } else if (['Home', 'End'].includes(e.key)) {
      e.stopPropagation();
    }
  };

  const dropdownProps = useMemo<DropDownProps>(() => {
    return {
      menu: {
        items: actions,
      },
      trigger: ['click'],
      placement: 'bottomRight',
    };
  }, [actions]);

  if (isEditingTitle) {
    return (
      <Input
        ref={inputRef}
        value={editingTitle}
        onChange={handleTitleChange}
        onKeyDown={handleTitleKeyDown}
        onBlur={() => handleTitleSubmit()}
        onClick={(e) => e.stopPropagation()}
        size="small"
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <div className="menu-sub-item-container">
      <Tooltip title={tooltip || label}>
        <div className="menu-sub-item-name-container">
          <Link className={classNames(isReadOnly && 'readonly')} to={path}>
            <div className="menu-sub-item-name">{label}</div>
          </Link>
        </div>
      </Tooltip>
      {isLoading ? (
        <Spin className="spin-container" size="small" />
      ) : (
        <div className="app-menu-actions" onClick={ignoreEvent}>
          <Dropdown {...dropdownProps}>
            <MoreOutlined
              className={classNames(
                'app-menu-actions-icon',
                isEditingTitle && 'active'
              )}
              onClick={ignoreEvent}
            />
          </Dropdown>
        </div>
      )}
    </div>
  );
};

export default MenuSubItem;
